import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Alert, Button } from 'react-bootstrap';

import { agreementDocumentLabelKey, useAgreementStatus } from './useAgreementStatus';

/**
 * The ordinary case: the organization accepted the current Terms and BAA at signup, and the admin
 * confirms them once from inside an authenticated session. A dismissible bar, never a gate — this
 * is a clinician-facing portal and a legal confirmation must not stand between a nurse and a
 * patient's readings. "Later" hides it for the session only, so it comes back at the next login
 * until it is actually acknowledged; nothing is persisted.
 */
const AgreementAcknowledgementBanner = () => {
  const intl = useIntl();
  const { applicable, needsAcknowledgement, dismissed, saving, items, dismiss, reaffirm } =
    useAgreementStatus();
  const [failed, setFailed] = useState(false);

  if (!applicable || !needsAcknowledgement || dismissed) return null;

  const documents = items
    .map(
      (item) =>
        `${intl.formatMessage({ id: agreementDocumentLabelKey(item.documentType) })} (${
          item.identifier
        })`
    )
    .join(intl.formatMessage({ id: 'billingAgreementDocumentSeparatorKey' }));

  const handleConfirm = async () => {
    setFailed(false);
    const succeeded = await reaffirm();
    if (!succeeded) setFailed(true);
  };

  return (
    <Alert variant="info" className="mb-3" data-test="billing-agreement-banner">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="me-3">
          <strong data-test="billing-agreement-banner-title">
            {intl.formatMessage({ id: 'billingAgreementBannerTitleKey' })}
          </strong>
          <div className="small" data-test="billing-agreement-banner-documents">
            {intl.formatMessage({ id: 'billingAgreementBannerBodyKey' }, { documents })}
          </div>
          {failed && (
            <div className="small text-danger" data-test="billing-agreement-banner-error">
              {intl.formatMessage({ id: 'billingAgreementErrorKey' })}
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="link"
            className="text-decoration-none"
            onClick={dismiss}
            disabled={saving}
            data-test="billing-agreement-banner-later"
          >
            {intl.formatMessage({ id: 'billingAgreementLaterKey' })}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={saving}
            data-test="billing-agreement-banner-confirm"
          >
            {intl.formatMessage({ id: 'billingAgreementConfirmKey' })}
          </Button>
        </div>
      </div>
    </Alert>
  );
};

export default AgreementAcknowledgementBanner;
